import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, TOKEN_KEY } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  const checkSession = useCallback(async () => {
    try {
      // Treat 401 as a valid "not logged in" answer rather than an error, so the
      // session probe never bubbles up as an uncaught axios rejection / dev overlay.
      const res = await api.get("/auth/me", { validateStatus: (s) => s === 200 || s === 401 });
      if (res.status === 200 && res.data?.user) setUser(res.data.user);
      else setUser((prev) => (prev ? prev : false)); // never overwrite a logged-in user
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Store token for the Authorization-header fallback (works when cookies are
      // blocked, e.g. inside the embedded iframe preview / strict privacy browsers).
      if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout failed:", e); }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const value = useMemo(() => ({ user, error, login, logout, refresh: checkSession }), [user, error, checkSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
